/* "Stop Managing The Pieces" — the 60-second disruptor spot (commercial #3).

   Disruptor, not a walkthrough: hard cuts, short punches, and the real product slammed on screen
   as proof. Every product frame is an actual Cornerstone Ops screenshot captured from a synthetic
   demo organization (scripts/capture_ad_shots.py) — never the operator's own business.

   Render rules learned the hard way on the weather film, all still in force:
     · layouts are pinned — changing text can never shove a panel
     · motion is whole-pixel, and screenshots never scale mid-shot, so nothing crawls or shimmers
     · text swaps fade, they never reflow
     · no invented weather alerts, no customer counts, no testimonials, no claims we can't back */
import { BrandBadge } from "@/components/BrandMark";
import {
  Calendar, Table2, MessageSquare, Mail, ClipboardCheck, Home, CloudSun, Globe, Users, Wrench,
  FileText, CreditCard, Check, ArrowRight, Bell, Zap,
} from "lucide-react";

const NAVY = "#070c1e";
const GOLD = "#d4af37";
const BLUE = "#8fb4ff";
const SHOT = (name) => `/ad3/shots/${name}.jpg`;

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const seg = (v, a, b) => {
  const x = clamp((v - a) / Math.max(b - a, 0.0001));
  return x * x * (3 - 2 * x);
};
const px = (n) => Math.round(n);

const Kicker = ({ children, u, o = 1, color = GOLD }) => (
  <p style={{ color, opacity: o, fontSize: `${1.7 * u}px`, fontWeight: 700,
              letterSpacing: `${0.5 * u}px`, textTransform: "uppercase" }}>{children}</p>
);

/* A real product screen, slammed into frame.

   The frame, its border and every piece of type are pinned to whole pixels and only cross-fade —
   they never move, so nothing can crawl. The screenshot itself lives in an inner layer that is
   moved only by a GPU transform, which is filtered smoothly. An earlier version nudged the whole
   frame in whole-pixel steps on entry and the picture visibly hopped three times as it appeared;
   that stepped motion is gone. */
const Plate = ({ shot, p, u, w, h, tall, ar = 1.6, pos = "50% 0%", arTall, posTall,
                 mark = null }) => {
  const inFrame = seg(p, 0, 0.12);
  const useAr = tall ? (arTall || ar) : ar;
  const usePos = tall ? (posTall || pos) : pos;
  const boxW = px(w * (tall ? 0.98 : 0.88));
  const regionTop = tall ? h * 0.17 : h * 0.13;
  const regionH = tall ? h * 0.58 : h * 0.62;
  let plateW = boxW;
  let plateH = px(boxW / useAr);
  if (plateH > regionH) {
    plateH = px(regionH);
    plateW = px(regionH * useAr);
  }
  const left = px((w - plateW) / 2);
  const top = px(regionTop + (regionH - plateH) / 2);
  // continuous, smooth push-in: no cut is ever dead still, and nothing steps
  const zoom = (1.012 + p * 0.03).toFixed(4);
  return (
    <div style={{ position: "absolute", left: `${left}px`, top: `${top}px`,
                  width: `${plateW}px`, height: `${plateH}px`, opacity: inFrame,
                  borderRadius: `${0.9 * u}px`, overflow: "hidden",
                  border: "1px solid rgba(212,175,55,0.45)",
                  boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
                  backgroundColor: "#0b1230" }}>
      <div style={{ position: "absolute", inset: 0,
                    backgroundImage: `url(${SHOT(shot)})`,
                    backgroundSize: "cover", backgroundPosition: usePos,
                    backgroundRepeat: "no-repeat",
                    transform: `scale(${zoom})`, transformOrigin: "50% 40%",
                    willChange: "transform" }} />
      {/* the one thing this cut is about, ringed in gold — square cut only, where the crop is known */}
      {mark && !tall && (
        <div style={{ position: "absolute",
          left: `${px(plateW * mark[0])}px`, top: `${px(plateH * mark[1])}px`,
          width: `${px(plateW * mark[2])}px`, height: `${px(plateH * mark[3])}px`,
          border: `${Math.max(2, px(u * 0.22))}px solid ${GOLD}`,
          borderRadius: `${0.5 * u}px`, opacity: seg(p, 0.3, 0.46),
          boxShadow: `0 0 ${2 * u}px rgba(212,175,55,0.55)` }} />
      )}
    </div>
  );
};

/* The line under the screen. Two stacked lines, both pinned, so neither can move the other. */
const Punch = ({ u, lead, lead2, o, o2, tall }) => (
  <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(tall ? 9 : 7) * u}px`,
                padding: "0 7%" }}>
    <h2 className="font-display" style={{ color: "#fff", fontWeight: 800,
      fontSize: `${(tall ? 4.4 : 4.6) * u}px`, lineHeight: 1.06, opacity: o }}>{lead}</h2>
    <h2 className="font-display" style={{ color: GOLD, fontWeight: 800,
      fontSize: `${(tall ? 4.4 : 4.6) * u}px`, lineHeight: 1.06, opacity: o2,
      marginTop: `${0.3 * u}px` }}>{lead2}</h2>
  </div>
);

const ProductCut = ({ p, u, w, h, tall, shot, kicker, lead, lead2, pos, mark, ar,
                      arTall, posTall }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <div className="absolute inset-0" style={{ background:
      "radial-gradient(95% 70% at 50% 8%, rgba(30,52,124,0.7), rgba(5,9,22,1) 72%)" }} />
    <div style={{ position: "absolute", left: 0, right: 0, top: `${(tall ? 9 : 6) * u}px`,
                  padding: "0 7%" }}>
      <Kicker u={u} o={seg(p, 0, 0.08)}>{kicker}</Kicker>
    </div>
    <Plate shot={shot} p={p} u={u} w={w} h={h} tall={tall} pos={pos} mark={mark} ar={ar}
           arTall={arTall} posTall={posTall} />
    <Punch u={u} tall={tall} lead={lead} lead2={lead2}
           o={seg(p, 0.2, 0.36)} o2={seg(p, 0.34, 0.52)} />
  </div>
);

/* ---- 0:00 · how many systems does it take ---------------------------------------------------- */
const TOOLS = [
  ["Calendar", Calendar], ["Spreadsheet", Table2], ["Texts", MessageSquare], ["Email", Mail],
  ["Inspection app", ClipboardCheck], ["Property list", Home], ["Weather site", CloudSun],
  ["Listing tabs", Globe], ["Owner thread", FileText], ["Vendor thread", Wrench],
  ["Crew roster", Users], ["Invoices", CreditCard],
];

const Chaos = ({ p, u, tall }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <div className="absolute inset-0" style={{ background:
      "radial-gradient(100% 80% at 50% 0%, rgba(30,48,112,0.55), rgba(5,9,22,1) 72%)" }} />
    {TOOLS.slice(0, tall ? 10 : 9).map(([label, Icon], i) => {
      const born = 0.04 + Math.pow(i / 9, 1.6) * 0.62;
      const on = seg(p, born, born + 0.07);
      const col = i % (tall ? 2 : 3);
      const row = Math.floor(i / (tall ? 2 : 3));
      const jitter = ((i * 37) % 7) - 3;
      return (
        <div key={label} style={{
          position: "absolute",
          left: `${(tall ? 6 : 5) + col * (tall ? 46 : 31)}%`,
          top: `${(tall ? 6 : 8) + row * (tall ? 15 : 21)}%`,
          width: `${tall ? 42 : 28}%`,
          opacity: on, transform: `translateY(${px((1 - on) * 2.5 * u + jitter)}px)` }}>
          <div style={{ display: "flex", alignItems: "center", gap: `${0.8 * u}px`,
                        background: "rgba(10,16,40,0.9)", border: "1px solid rgba(143,180,255,0.22)",
                        borderRadius: `${1 * u}px`, padding: `${1 * u}px ${1.2 * u}px` }}>
            <Icon style={{ width: 1.9 * u, height: 1.9 * u, color: BLUE, flexShrink: 0 }} />
            <span style={{ color: "#fff", fontSize: `${1.5 * u}px`, fontWeight: 700,
                           whiteSpace: "nowrap", overflow: "hidden" }}>{label}</span>
          </div>
          <div style={{ marginTop: `${0.5 * u}px`, height: `${2.6 * u}px`, borderRadius: `${0.6 * u}px`,
                        background: "linear-gradient(90deg, rgba(143,180,255,0.14), transparent)" }} />
        </div>
      );
    })}
    <div className="absolute inset-x-0" style={{ bottom: 0, padding: `0 7% ${5 * u}px`,
         background: "linear-gradient(to top, rgba(5,9,22,0.97) 15%, rgba(5,9,22,0.55) 75%, transparent)" }}>
      <h1 className="font-display" style={{
        color: "#fff", fontWeight: 800, fontSize: `${4.6 * u}px`, lineHeight: 1.08,
        opacity: seg(p, 0.12, 0.3), textShadow: "0 10px 40px rgba(0,0,0,0.8)" }}>
        How many systems does it take<br />
        <span style={{ color: GOLD }}>to run one property?</span>
      </h1>
    </div>
  </div>
);

/* ---- the break ------------------------------------------------------------------------------- */
const Break = ({ p, u }) => {
  const dark = seg(p, 0.0, 0.22);
  return (
    <div className="absolute inset-0 flex items-center justify-center text-center"
         style={{ background: "#03060f", padding: "8%" }}>
      <div className="absolute inset-0" style={{ opacity: 1 - dark, background:
        "radial-gradient(90% 70% at 50% 40%, rgba(30,48,112,0.5), rgba(3,6,15,1) 70%)" }} />
      <h2 className="font-display" style={{ position: "relative", color: "#fff", fontWeight: 800,
        fontSize: `${7 * u}px`, lineHeight: 1.04, opacity: seg(p, 0.2, 0.46) }}>
        There's a<br /><span style={{ color: GOLD }}>better way.</span>
      </h2>
    </div>
  );
};

/* ---- the product cuts ------------------------------------------------------------------------ */
const Command = (a) => (
  <ProductCut {...a} shot="dashboard" ar={1.6} pos="50% 0%"
    kicker="Operational command center" lead="Every property, every job," lead2="one screen." />
);

const Weather = (a) => (
  <ProductCut {...a} shot="weather" ar={2.0} pos="0% 0%"
    kicker="Weather intelligence" lead="Live conditions for every address —"
    lead2="from the National Weather Service." />
);

const Briefing = (a) => (
  <ProductCut {...a} shot="briefing" ar={0.78} pos="50% 0%"
    kicker="AI operational briefing" lead="Not just the forecast." lead2="What to do about it." />
);

const Automate = (a) => (
  <ProductCut {...a} shot="automations" ar={1.6} pos="50% 0%"
    kicker="Automations · when · if · then" lead="Set the rule once."
    lead2="The work creates itself." mark={[0.19, 0.23, 0.39, 0.2]} />
);

const Queue = (a) => (
  <ProductCut {...a} shot="schedule" ar={1.6} pos="50% 0%"
    kicker="Work orders" lead="Turnovers, repairs, inspections, vendors —" lead2="one queue." />
);

const Proof = (a) => (
  <ProductCut {...a} shot="workorder" ar={1.4} pos="50% 0%"
    kicker="Signed off on site" lead="Checklists, crews, cost, history."
    lead2="Proof, not guesswork." mark={[0.02, 0.47, 0.6, 0.5]} />
);

const Stewardship = (a) => (
  <ProductCut {...a} shot="inspections" ar={1.6} pos="50% 0%"
    kicker="Asset stewardship" lead="Condition scored and documented —"
    lead2="so problems aren't inherited." />
);

const Reporting = (a) => (
  <ProductCut {...a} shot="reports" ar={1.6} pos="50% 0%"
    kicker="Reports & analytics" lead="Completion rates. Fix times." lead2="Condition scores."
    mark={[0.18, 0.28, 0.79, 0.14]} />
);

const Access = (a) => (
  <ProductCut {...a} shot="team" ar={1.6} pos="50% 0%"
    kicker="Team & access" lead="Owners, managers, crews, vendors —"
    lead2="each sees only what they should." />
);

const Money = (a) => (
  <ProductCut {...a} shot="invoices" ar={1.6} pos="50% 0%"
    kicker="Invoicing & payouts" lead="Billed from the work itself." lead2="Nothing typed twice." />
);

const Trial = (a) => (
  <ProductCut {...a} shot="billing" ar={1.2} pos="50% 0%"
    kicker="Start in minutes" lead="Fourteen days free."
    lead2="A tier for every size of operation." />
);

/* ---- push alerts: drawn in-engine, because a real browser can't be granted a phone ----------- */
const ALERTS = [
  [Zap, "Automation ran", "Inspection created after turnover"],
  [Wrench, "Vendor dispatched", "HVAC repair — Sound View 7"],
  [CloudSun, "Conditions changed", "Wind advisory — secure exteriors"],
  [Check, "Work order complete", "Checklist 8/8 signed off"],
];

const Alerts = ({ p, u, w, h, tall }) => {
  const phoneW = px(tall ? w * 0.62 : h * 0.3);
  const phoneH = px(phoneW * (tall ? 1.5 : 1.95));
  const left = px((w - phoneW) / 2);
  const top = px(tall ? h * 0.2 : h * 0.15);
  const on = seg(p, 0, 0.12);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(85% 60% at 50% 20%, rgba(32,54,128,0.7), rgba(5,9,22,1) 72%)" }} />
      <div style={{ position: "absolute", left: 0, right: 0, top: `${(tall ? 9 : 6) * u}px`,
                    padding: "0 7%" }}>
        <Kicker u={u} o={seg(p, 0, 0.08)}>Push notifications · no text-message charges</Kicker>
      </div>
      <div style={{ position: "absolute", left: `${left}px`, top: `${top}px`, width: `${phoneW}px`,
        height: `${phoneH}px`, opacity: on, borderRadius: `${2.4 * u}px`,
        border: `${Math.max(2, px(u * 0.3))}px solid rgba(143,180,255,0.35)`,
        background: "linear-gradient(180deg, rgba(10,16,40,0.98), rgba(6,10,26,0.98))",
        boxShadow: "0 30px 80px rgba(0,0,0,0.6)", overflow: "hidden" }}>
        <div style={{ position: "absolute", left: "50%", top: `${1 * u}px`,
          width: `${px(phoneW * 0.3)}px`, height: `${0.7 * u}px`,
          marginLeft: `${px(-phoneW * 0.15)}px`, borderRadius: `${1 * u}px`,
          background: "rgba(143,180,255,0.25)" }} />
        {ALERTS.map(([Icon, title, body], i) => {
          const born = 0.14 + i * 0.15;
          const show = seg(p, born, born + 0.1);
          return (
            <div key={title} style={{ position: "absolute", left: `${px(phoneW * 0.05)}px`,
              top: `${px(phoneH * (0.08 + i * 0.13))}px`, width: `${px(phoneW * 0.9)}px`,
              opacity: show,
              background: "rgba(255,255,255,0.97)", borderRadius: `${1 * u}px`,
              padding: `${0.9 * u}px ${1 * u}px`, boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: `${0.6 * u}px` }}>
                <Icon style={{ width: 1.3 * u, height: 1.3 * u, color: "#1b2a63", flexShrink: 0 }} />
                <span style={{ color: "#101a3d", fontWeight: 800, fontSize: `${1.25 * u}px`,
                               whiteSpace: "nowrap" }}>{title}</span>
                <Bell style={{ width: 1 * u, height: 1 * u, color: "#8894b8", marginLeft: "auto" }} />
              </div>
              <p style={{ marginTop: `${0.35 * u}px`, color: "#44507a", fontWeight: 600,
                          fontSize: `${1.1 * u}px`, lineHeight: 1.25 }}>{body}</p>
            </div>
          );
        })}
      </div>
      <Punch u={u} tall={tall} lead="The second it changes," lead2="the crew already knows."
             o={seg(p, 0.3, 0.46)} o2={seg(p, 0.44, 0.6)} />
    </div>
  );
};

/* ---- it scales ------------------------------------------------------------------------------- */
const Scale = ({ p, u }) => {
  const count = p < 0.3 ? 5 : p < 0.62 ? 50 : 500;
  const label = count === 500 ? "500+" : `${count}`;
  const dots = Math.min(count, 180);
  const show = seg(p, 0.02, 0.2);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(85% 65% at 50% 40%, rgba(30,52,124,0.65), rgba(4,8,20,1) 74%)" }} />
      <div className="absolute inset-0 flex items-center justify-center" style={{ padding: "10%" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: `${0.7 * u}px`, justifyContent: "center",
                      maxWidth: "100%", opacity: show }}>
          {Array.from({ length: dots }).map((_, i) => (
            <span key={i} style={{ width: `${1.5 * u}px`, height: `${1.5 * u}px`,
              borderRadius: `${0.35 * u}px`,
              background: i % 7 === 0 ? GOLD : "rgba(143,180,255,0.55)",
              opacity: clamp((dots - i) / 24 + 0.25) }} />
          ))}
        </div>
      </div>
      <div className="absolute inset-x-0 text-center" style={{ top: "12%", padding: "0 8%" }}>
        <p className="font-display" style={{ color: "#fff", fontWeight: 800, fontSize: `${8 * u}px`,
           lineHeight: 1 }}>{label}</p>
        <Kicker u={u} o={0.9}>Properties</Kicker>
      </div>
      <div className="absolute inset-x-0 text-center" style={{ bottom: `${8 * u}px`, padding: "0 8%" }}>
        <h2 className="font-display" style={{ color: GOLD, fontWeight: 800, fontSize: `${4.4 * u}px`,
          lineHeight: 1.1, opacity: seg(p, 0.5, 0.7) }}>
          Built to scale the operation.
        </h2>
      </div>
    </div>
  );
};

/* ---- stop managing the pieces ---------------------------------------------------------------- */
const Cta = ({ p, t, u }) => {
  const a = seg(p, 0.02, 0.18);
  const b = seg(p, 0.2, 0.38);
  const c = seg(p, 0.34, 0.52);
  const verse = seg(p, 0.66, 0.88);
  const drift = px(Math.sin(t * 0.5) * 0.25 * u);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ background: NAVY, padding: "8%" }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(78% 56% at 50% 42%, rgba(38,66,152,0.6), rgba(4,8,20,1) 74%)" }} />
      <div style={{ position: "relative", transform: `translateY(${drift}px)` }}>
        <p className="font-display" style={{ color: "rgba(226,232,255,0.92)", fontWeight: 800,
           fontSize: `${5 * u}px`, lineHeight: 1.1, opacity: a }}>
          Stop managing the pieces.
        </p>
        <p className="font-display" style={{ color: GOLD, fontWeight: 800, fontSize: `${6.4 * u}px`,
           lineHeight: 1.06, opacity: b }}>
          Run the operation.
        </p>
        <div style={{ opacity: c, marginTop: `${3 * u}px` }}>
          <div className="flex justify-center">
            <div style={{ width: `${9 * u}px`, height: `${9 * u}px` }}>
              <BrandBadge size="h-full w-full" icon="h-3/5 w-3/5" />
            </div>
          </div>
          <h2 className="font-display" style={{ marginTop: `${1.8 * u}px`, color: "#fff",
             fontWeight: 800, fontSize: `${6 * u}px`, lineHeight: 1 }}>Cornerstone Ops</h2>
          <div style={{ height: 1, width: `${22 * u}px`, margin: `${1.8 * u}px auto`,
                        background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
          <p style={{ color: GOLD, fontSize: `${2 * u}px`, fontWeight: 700,
                      letterSpacing: `${0.55 * u}px`, textTransform: "uppercase" }}>
            The Operational Command Center
          </p>
          <p style={{ marginTop: `${1.8 * u}px`, color: "#fff", fontWeight: 700,
                      fontSize: `${2.3 * u}px` }}>cornerstoneops.io</p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: `${0.7 * u}px`,
            marginTop: `${1.6 * u}px`, background: GOLD, color: "#101a3d", fontWeight: 800,
            fontSize: `${2 * u}px`, padding: `${1.1 * u}px ${2.6 * u}px`, borderRadius: `${9 * u}px`,
            opacity: seg(p, 0.46, 0.64) }}>
            Start your 14 days <ArrowRight style={{ width: 1.6 * u, height: 1.6 * u }} />
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: `${5 * u}px`, opacity: verse,
                    padding: "0 8%" }}>
        <div style={{ height: 1, width: `${16 * u}px`, margin: `0 auto ${1.2 * u}px`,
                      background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <p style={{ color: GOLD, fontSize: `${1.4 * u}px`, fontWeight: 700,
                    letterSpacing: `${0.4 * u}px`, textTransform: "uppercase" }}>Psalm 24:1</p>
        <p style={{ marginTop: `${0.7 * u}px`, color: "rgba(216,228,255,0.85)",
                    fontSize: `${1.9 * u}px`, fontStyle: "italic" }}>
          "The earth is the LORD'S, and everything in it."
        </p>
      </div>
    </div>
  );
};

/* Voice cue -> scene. Several cues can share one scene; consecutive ones are merged by the page. */
export const DISRUPT_SCENE_OF = {
  chaos: "chaos", better: "break",
  meet: "command", command: "command",
  weather: "weather", briefing: "briefing",
  automate: "automate", queue: "queue",
  proof: "proof", condition: "stewardship",
  alerts: "alerts", reports: "reports",
  access: "access", money: "money",
  scale: "scale", trial: "trial",
  pieces: "cta", run: "cta",
};

const RENDERERS = {
  chaos: Chaos, break: Break, command: Command, weather: Weather, briefing: Briefing,
  automate: Automate, queue: Queue, proof: Proof, stewardship: Stewardship, alerts: Alerts,
  reports: Reporting, access: Access, money: Money, scale: Scale, trial: Trial, cta: Cta,
};

const PRELOAD = ["dashboard", "weather", "briefing", "automations", "schedule", "workorder",
                 "inspections", "reports", "team", "invoices", "billing"];

export default function DisruptorFilm({ t = 0, segments = [], w = 1080, h = 1080 }) {
  const u = Math.min(w, h) / 100;
  const tall = h / w > 1.25;
  const idx = segments.reduce((best, s, i) => (t >= s.start ? i : best), 0);
  const active = segments[idx];
  if (!active) return <div className="absolute inset-0" style={{ background: NAVY }} />;
  const Scene = RENDERERS[active.scene] || Chaos;
  const p = clamp((t - active.start) / Math.max(active.end - active.start, 0.001));
  // rise in, dip out: cuts dissolve rather than snap to black
  const into = Math.min(clamp((t - active.start) / 0.22),
                        1 - clamp((t - (active.end - 0.16)) / 0.16));
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}
         data-testid={`disrupt-scene-${active.scene}`}>
      {/* every screenshot is decoded before the first frame is filmed */}
      <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", opacity: 0.01 }}>
        {PRELOAD.map((name) => <img key={name} src={SHOT(name)} alt="" width="1" height="1" />)}
      </div>
      <div className="absolute inset-0" style={{ opacity: into }}>
        <Scene p={p} t={t - active.start} u={u} w={w} h={h} tall={tall} />
      </div>
      <div className="absolute inset-0" style={{ pointerEvents: "none", opacity: 0.45, background:
        `radial-gradient(58% 44% at ${(50 + 8 * Math.sin(t * 0.23)).toFixed(2)}% ${(45 + 6 * Math.cos(t * 0.19)).toFixed(2)}%,
         rgba(96,140,255,0.07), transparent 70%)` }} />
      <div className="absolute inset-0" style={{ opacity: 0.04, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: `${0.6 * u}px ${0.6 * u}px` }} />
    </div>
  );
}
